import React from 'react';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import countryList from 'country-list';
import PropTypes from 'prop-types';
import { assocPath, path as getByPath } from 'rambdax';
import { ShowLessArrowsIcon, ShowMoreArrowsIcon } from '@/assets/images';
import CustomLink from '@/components/common/CustomLink';
import AddressAutocomplete from '@/components/common/form/AddressAutocomplete';
import USAStateSelect from '@/components/common/USAStateSelect';
import translate from '@/lib/intl/translate';

const defaultAddress = {
  city: '',
  country: 'US',
  formattedAddress: '',
  gmapsAddressComponents: [],
  gmapsPlaceId: '',
  latitude: null,
  line1: '',
  line2: '',
  longitude: null,
  postalCode: '',
  state: '',
  unit: '',
};

const getInitialState = (data) =>
  Object.entries(defaultAddress).reduce(
    (obj, [key, val]) => ({
      ...obj,
      [key]: data?.[key] || val,
    }),
    {},
  );

export const mapAddressComponents = (addressComponents = []) => {
  const newAddress = {};
  addressComponents.forEach(({ shortName, types }) => {
    if (types.includes('street_number')) {
      newAddress.line1 = shortName;
    } else if (types.includes('route')) {
      newAddress.line1 = [
        newAddress.line1 ? `${newAddress.line1} ` : '',
        shortName,
      ].join('');
    } else if (types.includes('locality')) {
      newAddress.city = shortName;
    } else if (types.includes('administrative_area_level_1')) {
      newAddress.state = shortName;
    } else if (types.includes('postal_code')) {
      newAddress.postalCode = shortName;
    } else if (types.includes('country')) {
      newAddress.country = shortName;
    }
  });
  return newAddress;
};

function AddressInputs({ data, disabled, label, path, updateData }) {
  const [showManual, setShowManual] = React.useState(false);
  const [localState, setLocalState] = React.useState(
    getInitialState(path ? getByPath(path, data) : data),
  );
  const [formTouched, setFormTouched] = React.useState(false);
  const handleChange = (e) => {
    const { name, value } = e.target;
    setLocalState({ ...localState, [name]: value });
  };

  const handleAutocomplete = (
    formattedAddress,
    { addressComponents, geometry, placeId },
  ) => {
    setLocalState({
      ...localState,
      ...mapAddressComponents(addressComponents),
      latitude: geometry?.location.lat(),
      longitude: geometry?.location.lng(),
      gmapsAddressComponents: addressComponents,
      gmapsPlaceId: placeId,
      formattedAddress,
    });
  };

  React.useEffect(() => {
    if (!formTouched) {
      setFormTouched(true);
      return;
    }
    updateData(path ? assocPath(path, localState, data) : localState);
  }, [localState]);

  return (
    <div className="mb-4 grid grid-cols-2 items-end gap-4 lg:grid-cols-6">
      {showManual ? (
        <>
          <div className="col-span-full lg:col-span-4">
            <TextField
              disabled={disabled}
              label={translate('STREET_ADDRESS_LABEL')}
              value={localState.line1}
              onChange={handleChange}
              name="line1"
              autoComplete="address-line1"
              fullWidth
            />
          </div>
          <div className="col-span-full lg:col-span-2">
            <TextField
              disabled={disabled}
              label={translate('APT_LABEL')}
              value={localState.unit}
              onChange={handleChange}
              name="unit"
              fullWidth
            />
          </div>
          <div className="col-span-full lg:col-span-2">
            <TextField
              disabled={disabled}
              label={translate('CITY_LABEL')}
              value={localState.city}
              onChange={handleChange}
              name="city"
              fullWidth
            />
          </div>
          <div className="col-span-full lg:col-span-2">
            {localState.country.toUpperCase() === 'US' ? (
              <USAStateSelect
                disabled={disabled}
                className="w-full"
                value={localState.state}
                onChange={handleChange}
              />
            ) : (
              <TextField
                disabled={disabled}
                label={translate('STATE_LABEL')}
                value={localState.state}
                onChange={handleChange}
                name="state"
                fullWidth
              />
            )}
          </div>
          <div className="col-span-1">
            <TextField
              disabled={disabled}
              label={translate('ZIP_LABEL')}
              value={localState.postalCode}
              onChange={handleChange}
              name="postalCode"
              autoComplete="postal-code"
              fullWidth
            />
          </div>
          <div className="col-span-1">
            <FormControl disabled={disabled}>
              <InputLabel>{translate('COUNTRY_LABEL')}</InputLabel>
              <Select
                disabled={disabled}
                label={translate('COUNTRY_LABEL')}
                name="country"
                native
                onChange={handleChange}
                value={localState.country}
              >
                {countryList.getData().map(({ code, name }) => (
                  <option value={code.toUpperCase()} key={code}>
                    {name}
                  </option>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className="col-span-full">
            <CustomLink
              component="button"
              onClick={() => setShowManual(false)}
              className="flex items-center"
              type="button"
            >
              <span className="mr-2">
                {translate('SHOW_LESS_OPTIONS_LABEL')}
              </span>
              <ShowLessArrowsIcon />
            </CustomLink>
          </div>
        </>
      ) : (
        <div className="col-span-full">
          <AddressAutocomplete
            disabled={disabled}
            label={label}
            className="mb-4"
            onChange={handleAutocomplete}
            defaultValue={localState.formattedAddress}
          />
          <CustomLink
            component="button"
            onClick={() => setShowManual(true)}
            className="flex items-center"
            type="button"
          >
            <span className="mr-2">{translate('SHOW_MORE_OPTIONS_LABEL')}</span>
            <ShowMoreArrowsIcon />
          </CustomLink>
        </div>
      )}
    </div>
  );
}

AddressInputs.propTypes = {
  data: PropTypes.object,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  path: PropTypes.string,
  updateData: PropTypes.func,
};

AddressInputs.defaultProps = {
  data: {},
  disabled: false,
  path: '',
  label: translate('FORMS_ADDRESS_LABEL'),
  updateData: () => {},
};

export default AddressInputs;
